// AGC: useWeather Hook - Weather data fetching with subscription tier integration
// AGC: Provides real-time weather data from Open-Meteo API
// AGC: FREE: 7 days, PRO/PREMIUM: 14 days forecast
// AGC: Location priority: 1) GPS, 2) User profile (city/state), 3) Default

import { useState, useEffect, useCallback, useRef } from 'react'
import { useSubscription } from '../context/SubscriptionContext'
import { useAuth } from './useAuth'
import {
  getWeatherData,
  getCurrentLocation,
  getForecastDays,
  clearWeatherCache,
} from '../services/weatherService'
import type { WeatherData, CurrentWeather, HourlyForecast, DailyForecast, SprayConditions } from '../types'

/**
 * AGC: Location source for tracking where coordinates came from
 */
type LocationSource = 'gps' | 'profile' | 'default' | 'custom'

/**
 * AGC: Hook options
 */
interface UseWeatherOptions {
  /** AGC: Custom coordinates (overrides auto-detection) */
  coordinates?: { lat: number; lon: number }
  /** AGC: Auto-refresh interval in milliseconds (0 = disabled) */
  refreshInterval?: number
  /** AGC: Skip initial fetch */
  skipInitialFetch?: boolean
}

/**
 * AGC: Hook return type
 */
interface UseWeatherReturn {
  // AGC: Weather data
  weather: WeatherData | null
  current: CurrentWeather | null
  hourly: HourlyForecast[]
  daily: DailyForecast[]
  sprayConditions: SprayConditions | null
  location: string
  
  // AGC: State
  isLoading: boolean
  error: string | null
  lastUpdated: Date | null
  
  // AGC: Tier info
  tier: string
  forecastDays: number
  
  // AGC: Coordinates
  coordinates: { lat: number; lon: number } | null
  locationSource: LocationSource | null
  
  // AGC: Actions
  refetch: (forceRefresh?: boolean) => Promise<void>
  clearCache: () => void
  setCoordinates: (lat: number, lon: number) => void
  detectLocation: () => Promise<void>
}

/**
 * AGC: useWeather - Main hook for weather data
 * @param options - Hook configuration options
 * @returns Weather data and controls
 */
export const useWeather = (options: UseWeatherOptions = {}): UseWeatherReturn => {
  const { coordinates: customCoordinates, refreshInterval = 0, skipInitialFetch = false } = options
  
  // AGC: Get subscription tier and user profile
  const { tier: subscriptionTier } = useSubscription()
  const { user } = useAuth()
  
  // AGC: State
  const [weather, setWeather] = useState<WeatherData | null>(null)
  const [coordinates, setCoordinatesState] = useState<{ lat: number; lon: number } | null>(
    customCoordinates || null
  )
  const [locationSource, setLocationSource] = useState<LocationSource | null>(
    customCoordinates ? 'custom' : null
  )
  const [isLoading, setIsLoading] = useState(!skipInitialFetch)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  
  // AGC: Refs for interval management
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = useRef(true)

  // AGC: Derive tier string
  const tier = subscriptionTier || 'free'
  const forecastDays = getForecastDays(tier)
  
  // AGC: Get user profile location for fallback
  const profileCity = user?.profile?.city
  const profileState = user?.profile?.state

  /**
   * AGC: Fetch weather data
   */
  const fetchWeather = useCallback(async (forceRefresh: boolean = false) => {
    if (!coordinates) {
      console.log('⚠️ AGC: No coordinates set for weather fetch')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      console.log(`🌤️ AGC: Fetching weather for (${coordinates.lat}, ${coordinates.lon}) - tier: ${tier}`)
      
      const data = await getWeatherData(coordinates.lat, coordinates.lon, tier, forceRefresh)
      
      if (isMountedRef.current) {
        setWeather(data)
        setLastUpdated(new Date())
        setError(null)
        console.log(`✅ AGC: Weather loaded - ${data.daily.length} days forecast`)
      }
    } catch (err) {
      console.error('❌ AGC: Weather fetch error:', err)
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar dados meteorológicos')
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false)
      }
    }
  }, [coordinates, tier])

  /**
   * AGC: Detect user's current location
   * AGC: Priority: 1) GPS, 2) User profile (city/state), 3) Default
   */
  const detectLocation = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      console.log('📍 AGC: Detecting user location...')
      console.log(`📍 AGC: Profile location fallback: ${profileCity || 'none'}, ${profileState || 'none'}`)
      
      // AGC: getCurrentLocation handles the priority: GPS -> Profile -> Default
      const location = await getCurrentLocation(profileCity, profileState)
      
      if (isMountedRef.current) {
        setCoordinatesState({ lat: location.lat, lon: location.lon })
        setLocationSource(location.source)
        console.log(`✅ AGC: Location set from ${location.source}: (${location.lat}, ${location.lon})`)
      }
    } catch (err) {
      console.error('❌ AGC: Location detection failed:', err)
      if (isMountedRef.current) {
        setError('Não foi possível detectar localização')
      }
    }
  }, [profileCity, profileState])

  /**
   * AGC: Set coordinates manually
   */
  const setCoordinates = useCallback((lat: number, lon: number) => {
    setCoordinatesState({ lat, lon })
    setLocationSource('custom')
  }, [])

  /**
   * AGC: Clear weather cache
   */
  const clearCache = useCallback(() => {
    clearWeatherCache()
    setWeather(null)
    setLastUpdated(null)
  }, [])

  /**
   * AGC: Refetch weather data
   */
  const refetch = useCallback(async (forceRefresh: boolean = true) => {
    await fetchWeather(forceRefresh)
  }, [fetchWeather])

  // AGC: Sync coordinates when customCoordinates prop changes; detect GPS only on first mount with no coords
  const didMountRef = useRef(false)
  useEffect(() => {
    if (customCoordinates) {
      setCoordinatesState(customCoordinates)
      setLocationSource('custom')
    } else if (!didMountRef.current && !coordinates) {
      detectLocation()
    }
    didMountRef.current = true
  }, [customCoordinates]) // eslint-disable-line react-hooks/exhaustive-deps

  // AGC: Fetch weather when coordinates change
  useEffect(() => {
    if (coordinates && !skipInitialFetch) {
      fetchWeather(false)
    }
  }, [coordinates, tier, skipInitialFetch])

  // AGC: Set up auto-refresh interval
  useEffect(() => {
    if (refreshInterval > 0 && coordinates) {
      intervalRef.current = setInterval(() => {
        fetchWeather(false)
      }, refreshInterval)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [refreshInterval, coordinates, fetchWeather])

  // AGC: Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // AGC: Derive values from weather data
  const current = weather?.current || null
  const hourly = weather?.hourly || []
  const daily = weather?.daily || []
  const sprayConditions = weather?.sprayConditions || null
  const location = weather?.location || ''

  return {
    weather,
    current,
    hourly,
    daily,
    sprayConditions,
    location,
    isLoading,
    error,
    lastUpdated,
    tier,
    forecastDays,
    coordinates,
    locationSource,
    refetch,
    clearCache,
    setCoordinates,
    detectLocation,
  }
}

export default useWeather

